import React, { useEffect, useRef, useState } from 'react'
import { message } from 'antd'

/**
 * WaveformCanvas
 *
 * Fetches an audio file from audioUrl, decodes it via AudioContext,
 * and renders a visual waveform onto a <canvas>.
 */
const WaveformCanvas = ({
  audioUrl,
  color = '#2563eb',
  height = 40,
  barWidth = 2,
  barGap = 1,
  loadingColor = '#e5e7eb'
}) => {
  const canvasRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [buffer, setBuffer] = useState(null)

  useEffect(() => {
    if (!audioUrl) return

    let isMounted = true
    setLoading(true)

    const fetchAndDecode = async () => {
      try {
        const response = await fetch(audioUrl)
        const arrayBuffer = await response.arrayBuffer()
        const audioCtx = new (window.AudioContext ||
          window.webkitAudioContext)()
        const decodedBuffer = await audioCtx.decodeAudioData(arrayBuffer)

        if (isMounted) {
          setBuffer(decodedBuffer)
          setLoading(false)
        }
      } catch (err) {
        console.error('WaveformCanvas error:', err)
        if (isMounted) {
          setLoading(false)
          // Don't show message.error here to avoid spamming for every failed segment
        }
      }
    }

    fetchAndDecode()

    return () => {
      isMounted = false
    }
  }, [audioUrl])

  useEffect(() => {
    if (!buffer || !canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const { width, height: canvasHeight } = canvas.getBoundingClientRect()

    // Handle high DPI displays
    const dpr = window.devicePixelRatio || 1
    canvas.width = width * dpr
    canvas.height = canvasHeight * dpr
    ctx.scale(dpr, dpr)

    const rawData = buffer.getChannelData(0) // Use first channel
    const samples = rawData.length
    const points = Math.floor(width / (barWidth + barGap))
    const blockSize = Math.floor(samples / points)
    const filteredData = []

    for (let i = 0; i < points; i++) {
      let blockStart = blockSize * i
      let sum = 0
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[blockStart + j])
      }
      filteredData.push(sum / blockSize)
    }

    // Draw
    ctx.clearRect(0, 0, width, canvasHeight)
    ctx.fillStyle = color
    const multiplier = canvasHeight * 0.8 // Scale factor

    filteredData.forEach((val, i) => {
      const x = i * (barWidth + barGap)
      const h = Math.max(2, val * multiplier)
      const y = (canvasHeight - h) / 2
      ctx.fillRect(x, y, barWidth, h)
    })
  }, [buffer, color, barWidth, barGap])

  return (
    <div
      style={{
        width: '100%',
        height,
        background: loading ? loadingColor : 'transparent',
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            color: '#9ca3af'
          }}
        >
          Loading waveform...
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: buffer ? 'block' : 'none'
        }}
      />
    </div>
  )
}

export default WaveformCanvas
